/* Playo — Scheduler for one channel (/mainlayout/playo/channels/:id).
   - Day / week timeline in the CHANNEL's timezone (stored as UTC).
   - "Add from Media Library": pick many movies / clips / whole series at once
     in the CMS Media Library; they're laid back to back from a start time
     (a series as all its episodes, in order).
   - Click an empty spot (or "Add item") → add one video from the Media
     Library. Starts auto-snap to the previous item's end.
   - Drag an item to move it (keeps its duration, snaps the same way).
   - Gaps are shown as hatched blocks: the filler plays there.
   - Actions: Fill gaps with filler, Copy day to…, Repeat daily/weekly until…
   - Items whose asset isn't channel-ready are flagged; scheduling one warns
     (the backend queues its transcode, filler plays until it's ready).
   Pure time/schedule logic: ./playoTime.js. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import um from "../userManagement/userManagement.module.css";
import s from "./Playo.module.css";
import { useRbac } from "../../rbac/RbacContext";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getChannel,
  getSchedule,
  createItem,
  updateItem,
  deleteItem,
  bulkCreate,
  resolveSelection,
  apiErrorMessage,
} from "./playoService";
import {
  DAY,
  MINUTE,
  addDays,
  dayRangeUtc,
  dayDeltas,
  findGaps,
  firstOverlap,
  formatDuration,
  formatTime,
  instantAtOffset,
  lastEndBefore,
  layoutDay,
  normalizeItems,
  parseHm,
  planCopies,
  planBlock,
  planFillGaps,
  repeatDates,
  snapStart,
  todayIn,
  toIso,
  weekStart,
  zonedParts,
  zonedToUtc,
} from "./playoTime";
import { LibraryPicker, ChannelReadyStatus, ReadyPill, useChannelReady } from "./PlayoShared";
import PlayoPreview from "./PlayoPreview";

const PX_PER_MIN = { day: 1.2, week: 0.7 };
const MAX_FETCH_DAYS = 31;

const weekdayLabel = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
};

/** Load items overlapping [from, to) in ≤ 31-day calls. */
async function loadRange(channelId, from, to) {
  const out = new Map();
  for (let a = from; a < to; a += MAX_FETCH_DAYS * DAY) {
    const b = Math.min(to, a + MAX_FETCH_DAYS * DAY);
    (await getSchedule(channelId, a, b)).forEach((it) => out.set(it.id, it));
  }
  return [...out.values()];
}

/** Items that START on a local day. */
const startingOn = (items, dateStr, tz) => {
  const { start, end } = dayRangeUtc(dateStr, tz);
  return normalizeItems(items).filter((it) => it.start >= start && it.start < end);
};

/** Opens a date/time input's native picker on a click anywhere in the field, not just its icon. */
function openPicker(e) {
  try {
    e.currentTarget.showPicker?.();
  } catch {
    // showPicker throws when the input is disabled or the browser blocks it; the field still works.
  }
}

/* ───────────────────────────── item drawer ───────────────────────────── */

function ItemForm({ channel, item, defaults, items, canEdit, onDone, onCancel }) {
  const tz = channel.timezone || "UTC";
  const isEdit = Boolean(item?.id);
  // External-stream channels: items are guide entries (title + time) for the live stream.
  const guideOnly = channel.source_type === "external";
  const startMs = item ? new Date(item.start_time).getTime() : defaults.start;
  const p0 = zonedParts(startMs, tz);
  const [form, setForm] = useState({
    type: item?.type || (guideOnly ? "live" : "vod"),
    asset_id: item?.asset_id || null,
    asset_name: item?.title || "",
    date: p0.date,
    time: `${String(p0.hour).padStart(2, "0")}:${String(p0.minute).padStart(2, "0")}${p0.second ? `:${String(p0.second).padStart(2, "0")}` : ""}`,
    duration_min: item ? String(Math.round((item.duration_sec / 60) * 100) / 100) : "",
    title_override: item?.title_override || "",
    snap: !isEdit,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(false);
  const ready = useChannelReady(!guideOnly && form.type !== "ad_break" ? form.asset_id : null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const notReady = form.asset_id && ready.state && !ready.state.channel_ready;

  const computedStart = () => {
    const minutes = parseHm(form.time);
    if (minutes === null || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) return null;
    const t = zonedToUtc(form.date, minutes, tz);
    return form.snap ? snapStart(t, items, { ignoreId: item?.id }) : t;
  };
  const preview = computedStart();

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (guideOnly && !form.title_override.trim()) errs.title_override = "Give the programme a title";
    if (!guideOnly && form.type !== "ad_break" && !form.asset_id) errs.asset_id = "Choose an asset";
    if (preview === null) errs.start_time = "Enter a date and a time (HH:mm)";
    let duration;
    if (form.duration_min !== "") {
      const v = Number(form.duration_min);
      if (!(v > 0) || v > 24 * 60) errs.duration_sec = "Duration must be between a few seconds and 24 h";
      else duration = Math.round(v * 60);
    } else if (form.type === "ad_break") errs.duration_sec = "Ad breaks need a duration";
    else if (guideOnly) errs.duration_sec = "How long does this programme run?";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const body = { type: form.type, start_time: toIso(preview), title_override: form.title_override.trim() || null };
    if (guideOnly || form.type === "ad_break") body.asset_id = null;
    else body.asset_id = form.asset_id;
    if (duration) body.duration_sec = duration;
    else if (isEdit && form.asset_id !== item.asset_id) {
      /* new asset, no duration given → backend defaults to the new asset's duration */
    } else if (isEdit) body.duration_sec = item.duration_sec;

    setSaving(true);
    try {
      const { message } = isEdit ? await updateItem(channel.id, item.id, body) : await createItem(channel.id, body);
      showToast(message || "Saved", /warning/i.test(message) ? "warning" : "success");
      onDone();
    } catch (err) {
      const fe = err?.response?.data?.errors;
      if (fe && typeof fe === "object") setErrors(fe);
      showToast(apiErrorMessage(err, "Could not save the item"), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteItem(channel.id, item.id);
      showToast("Item removed", "success");
      onDone();
    } catch (err) {
      showToast(apiErrorMessage(err, "Could not remove the item"), "error");
      setSaving(false);
    }
  };

  const prevEnd = lastEndBefore(preview ?? startMs, items.filter((x) => x.id !== item?.id));

  return (
    <form onSubmit={submit} className={um.drawerBody} aria-label={isEdit ? "Edit item" : "Add item"}>
      {guideOnly && (
        <label className={um.field}>
          <span className={um.label}>Programme title *</span>
          <input className={um.input} value={form.title_override} onChange={set("title_override")} disabled={!canEdit} aria-label="Programme title" placeholder="e.g. Morning News" />
          <span className={um.hint}>Shown in the TV guide while the live stream plays. It doesn't change the stream.</span>
          {errors.title_override && <span className={um.errorText}>{errors.title_override}</span>}
        </label>
      )}
      {!guideOnly && (
      <label className={um.field}>
        <span className={um.label}>Type</span>
        <select className={um.select} value={form.type} onChange={set("type")} disabled={!canEdit} aria-label="Type">
          <option value="vod">Programme (VOD)</option>
          <option value="filler">Filler</option>
          <option value="ad_break">Ad break (plays filler in v1)</option>
        </select>
      </label>
      )}
      {!guideOnly && form.type !== "ad_break" && (
        <div className={um.field}>
          <span className={um.label}>Asset *</span>
          <div className={s.readyRow}>
            <span>{form.asset_id ? form.asset_name || `Video #${form.asset_id}` : <span className={s.muted}>None</span>}</span>
            {canEdit && (
              <button type="button" className={um.btnSubtle} onClick={() => setPicking(true)}>
                {form.asset_id ? "Change" : "Choose from Media Library"}
              </button>
            )}
          </div>
          {form.asset_id && <ChannelReadyStatus key={form.asset_id} videoId={form.asset_id} canEdit={canEdit} />}
          {notReady && (
            <span className={s.warnText} data-testid="not-ready-warning">
              This asset isn't channel-ready. Saving queues its transcode; the filler plays in its slot until it's ready.
            </span>
          )}
          {errors.asset_id && <span className={um.errorText}>{errors.asset_id}</span>}
        </div>
      )}
      <div className={um.fieldRow}>
        <label className={um.field}>
          <span className={um.label}>Date ({channel.timezone})</span>
          <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={form.date} onChange={set("date")} disabled={!canEdit} aria-label="Date" />
        </label>
        <label className={um.field}>
          <span className={um.label}>Start time</span>
          <input type="time" step={form.time.split(":").length === 3 ? 1 : 60} className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={form.time} onChange={set("time")} disabled={!canEdit} aria-label="Start time" />
        </label>
      </div>
      <label className={s.checkRow}>
        <input type="checkbox" checked={form.snap} onChange={set("snap")} disabled={!canEdit} />
        Snap to the end of the previous item
        {prevEnd !== null && <span className={s.muted}> (ends {formatTime(prevEnd, channel.timezone)})</span>}
      </label>
      {preview !== null && form.snap && (
        <span className={um.hint}>
          Starts at {formatTime(preview, channel.timezone)} ({toIso(preview)} UTC)
        </span>
      )}
      {errors.start_time && <span className={um.errorText}>{errors.start_time}</span>}
      <label className={um.field}>
        <span className={um.label}>Duration (minutes){guideOnly ? " *" : ""}</span>
        <input className={um.input} value={form.duration_min} onChange={set("duration_min")} placeholder={guideOnly ? "e.g. 60" : "the asset's length"} disabled={!canEdit} aria-label="Duration (minutes)" />
        {errors.duration_sec && <span className={um.errorText}>{errors.duration_sec}</span>}
      </label>
      {!guideOnly && (
        <label className={um.field}>
          <span className={um.label}>Title override</span>
          <input className={um.input} value={form.title_override} onChange={set("title_override")} disabled={!canEdit} aria-label="Title override" />
        </label>
      )}
      <div className={um.drawerFooter}>
        {isEdit && canEdit && (
          <button type="button" className={um.btnDanger} onClick={remove} disabled={saving}>
            Remove
          </button>
        )}
        <button type="button" className={um.btnGhost} onClick={onCancel}>
          Cancel
        </button>
        {canEdit && (
          <button type="submit" className={um.btnPrimary} disabled={saving}>
            {saving ? "Saving…" : notReady ? "Schedule anyway" : isEdit ? "Save" : guideOnly ? "Add to guide" : "Add to schedule"}
          </button>
        )}
      </div>
      <LibraryPicker
        open={picking}
        kind="video"
        onClose={() => setPicking(false)}
        onPick={(v) => setForm((f) => ({ ...f, asset_id: v.id, asset_name: v.title || v.name || "", duration_min: isEdit ? "" : f.duration_min }))}
      />
    </form>
  );
}

/* ───────────────────────────── action dialogs ───────────────────────────── */

function ActionDialog({ title, children, onCancel, onConfirm, confirmLabel, busy, disabled }) {
  return (
    <div className={um.dialogOverlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={um.dialog}>
        <h3 className={um.dialogTitle}>{title}</h3>
        <div className={s.dialogBody}>{children}</div>
        <div className={um.dialogActions}>
          <button type="button" className={um.btnGhost} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={um.btnPrimary} onClick={onConfirm} disabled={busy || disabled}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function FillGapsDialog({ channel, days, onCancel, onDone }) {
  const tz = channel.timezone || "UTC";
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const from = Math.max(dayRangeUtc(days[0], tz).start, Date.now());
  const to = dayRangeUtc(days[days.length - 1], tz).end;

  useEffect(() => {
    let alive = true;
    loadRange(channel.id, from - DAY, to + DAY).then((items) => {
      if (!alive) return;
      const gaps = findGaps(items, from, to);
      setPlan({ gaps, items: planFillGaps(gaps, channel.filler_asset_id) });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.id]);

  const total = plan ? plan.gaps.reduce((a, g) => a + (g.end - g.start) / 1000, 0) : 0;
  const run = async () => {
    setBusy(true);
    try {
      const { created } = await bulkCreate(channel.id, plan.items);
      showToast(`Added ${created} filler item${created === 1 ? "" : "s"}`, "success");
      onDone();
    } catch (e) {
      showToast(apiErrorMessage(e, "Could not fill the gaps"), "error");
      setBusy(false);
    }
  };

  return (
    <ActionDialog title="Fill gaps with filler" onCancel={onCancel} onConfirm={run} confirmLabel="Fill gaps" busy={busy} disabled={!plan || !plan.items.length || !channel.filler_asset_id}>
      {!channel.filler_asset_id ? (
        <p className={um.errorText}>This channel has no filler asset. Set one in the channel settings first.</p>
      ) : !plan ? (
        <p className={s.muted}>Finding gaps…</p>
      ) : plan.gaps.length === 0 ? (
        <p>No gaps from now to the end of {weekdayLabel(days[days.length - 1])}.</p>
      ) : (
        <p data-testid="fill-summary">
          {plan.gaps.length} gap{plan.gaps.length === 1 ? "" : "s"} ({formatDuration(total)}) from now to the end of {weekdayLabel(days[days.length - 1])} will get filler items.
        </p>
      )}
    </ActionDialog>
  );
}

function CopyDayDialog({ channel, sourceDate, onCancel, onDone }) {
  const tz = channel.timezone || "UTC";
  const [src, setSrc] = useState(sourceDate);
  const [target, setTarget] = useState(addDays(sourceDate, 1));
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    if (src === target) {
      setError("Pick a different target day");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const sRange = dayRangeUtc(src, tz);
      const tRange = dayRangeUtc(target, tz);
      const source = startingOn(await loadRange(channel.id, sRange.start, sRange.end), src, tz);
      if (!source.length) {
        // Never clear the target from an empty source, even with Replace on:
        // it would wipe programmes about to air with no undo.
        setError("Nothing starts on the source day. The target day was left as it was.");
        setBusy(false);
        return;
      }
      let existing = await loadRange(channel.id, tRange.start - DAY, tRange.end + DAY);
      if (replace) {
        // Planned as if the target day were empty; the server deletes it and
        // inserts the copies in ONE transaction, so a failure changes nothing.
        const doomed = new Set(startingOn(existing, target, tz).map((x) => x.id));
        existing = existing.filter((x) => !doomed.has(x.id));
      }
      const { items, skipped } = planCopies(source, dayDeltas(src, [target], tz), existing);
      if (replace && !items.length) {
        setError("Nothing to copy: every item would overlap. The target day was left as it was.");
        setBusy(false);
        return;
      }
      const { created } = items.length
        ? await bulkCreate(channel.id, items, replace ? { replace: { from: tRange.start, to: tRange.end } } : {})
        : { created: 0 };
      showToast(`Copied ${created} item${created === 1 ? "" : "s"}${skipped ? `, skipped ${skipped} that would overlap` : ""}`, skipped ? "warning" : "success");
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not copy the day"));
      setBusy(false);
    }
  };

  return (
    <ActionDialog title="Copy day to…" onCancel={onCancel} onConfirm={run} confirmLabel="Copy" busy={busy}>
      <div className={um.fieldRow}>
        <label className={um.field}>
          <span className={um.label}>Copy</span>
          <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={src} onChange={(e) => setSrc(e.target.value)} aria-label="Source day" />
        </label>
        <label className={um.field}>
          <span className={um.label}>To</span>
          <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Target day" />
        </label>
      </div>
      <label className={s.checkRow}>
        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} aria-label="Replace target day" />
        Replace what's already on the target day
      </label>
      <span className={um.hint}>Times are kept in {tz}. Items that would overlap are skipped.</span>
      {error && <span className={um.errorText}>{error}</span>}
    </ActionDialog>
  );
}

function RepeatDialog({ channel, sourceDate, onCancel, onDone }) {
  const tz = channel.timezone || "UTC";
  const [src, setSrc] = useState(sourceDate);
  const [every, setEvery] = useState("1");
  const [until, setUntil] = useState(addDays(sourceDate, 7));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const targets = /^\d{4}-\d{2}-\d{2}$/.test(until) ? repeatDates(src, Number(every), until) : [];

  const run = async () => {
    if (!targets.length) {
      setError("'Until' must be after the source day");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const sRange = dayRangeUtc(src, tz);
      const source = startingOn(await loadRange(channel.id, sRange.start, sRange.end), src, tz);
      if (!source.length) {
        setError("Nothing starts on the source day");
        setBusy(false);
        return;
      }
      const existing = await loadRange(channel.id, dayRangeUtc(targets[0], tz).start - DAY, dayRangeUtc(targets[targets.length - 1], tz).end + DAY);
      const { items, skipped } = planCopies(source, dayDeltas(src, targets, tz), existing);
      const { created } = items.length ? await bulkCreate(channel.id, items) : { created: 0 };
      showToast(`Added ${created} item${created === 1 ? "" : "s"} on ${targets.length} day${targets.length === 1 ? "" : "s"}${skipped ? `, skipped ${skipped} that would overlap` : ""}`, skipped ? "warning" : "success");
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not repeat the day"));
      setBusy(false);
    }
  };

  return (
    <ActionDialog title="Repeat…" onCancel={onCancel} onConfirm={run} confirmLabel="Repeat" busy={busy}>
      <div className={um.fieldRow}>
        <label className={um.field}>
          <span className={um.label}>Repeat day</span>
          <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={src} onChange={(e) => setSrc(e.target.value)} aria-label="Repeat source day" />
        </label>
        <label className={um.field}>
          <span className={um.label}>Every</span>
          <select className={um.select} value={every} onChange={(e) => setEvery(e.target.value)} aria-label="Frequency">
            <option value="1">Day</option>
            <option value="7">Week</option>
          </select>
        </label>
        <label className={um.field}>
          <span className={um.label}>Until</span>
          <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={until} onChange={(e) => setUntil(e.target.value)} aria-label="Until" />
        </label>
      </div>
      <span className={um.hint}>
        {targets.length} day{targets.length === 1 ? "" : "s"}. Times are kept in {tz}. Items that would overlap are skipped.
      </span>
      {error && <span className={um.errorText}>{error}</span>}
    </ActionDialog>
  );
}

/* ───────────────────────────── page ───────────────────────────── */

/* ───────────── add many from the Media Library (movies + whole series) ───────────── */

function BulkAddDialog({ channel, picks, defaultStart, onCancel, onDone }) {
  const tz = channel.timezone || "UTC";
  const p0 = zonedParts(defaultStart, tz);
  const [resolved, setResolved] = useState(null); // { items, skipped }
  const [error, setError] = useState("");
  const [date, setDate] = useState(p0.date);
  const [time, setTime] = useState(`${String(p0.hour).padStart(2, "0")}:${String(p0.minute).padStart(2, "0")}`);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    let alive = true;
    resolveSelection(picks)
      .then((r) => alive && setResolved(r))
      .catch((e) => alive && setError(apiErrorMessage(e, "Could not load the selected media")));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minutes = parseHm(time);
  const startMs = minutes !== null && /^\d{4}-\d{2}-\d{2}$/.test(date) ? zonedToUtc(date, minutes, tz) : null;
  const plan = resolved && startMs !== null ? planBlock(resolved.items, startMs) : null;
  const skippedAll = [...(resolved?.skipped || []), ...(plan?.skipped || [])];

  // Existing items inside the new block (loaded fresh, so it covers days off screen too).
  useEffect(() => {
    if (!plan || !plan.items.length) {
      setConflict(null);
      return undefined;
    }
    let alive = true;
    const end = plan.end;
    loadRange(channel.id, startMs - DAY, end + DAY)
      .then((existing) => alive && setConflict(firstOverlap(existing, startMs, end)))
      .catch(() => alive && setConflict(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.id, startMs, plan?.end, plan?.items.length]);

  const run = async () => {
    setBusy(true);
    try {
      const { created, notReady } = await bulkCreate(channel.id, plan.items);
      const pending = (notReady || []).length;
      showToast(
        `Added ${created} item${created === 1 ? "" : "s"}${pending ? `. ${pending} video${pending === 1 ? " is" : "s are"} being made channel-ready; the filler plays in their slots until then.` : ""}`,
        pending ? "warning" : "success"
      );
      onDone(startMs);
    } catch (e) {
      showToast(apiErrorMessage(e, "Could not add the items"), "error");
      setBusy(false);
    }
  };

  const total = plan ? plan.placed.reduce((a, x) => a + x.duration_sec, 0) : 0;
  return (
    <ActionDialog
      title="Add from Media Library"
      onCancel={onCancel}
      onConfirm={run}
      confirmLabel={plan && plan.items.length ? `Add ${plan.items.length} item${plan.items.length === 1 ? "" : "s"}` : "Add"}
      busy={busy}
      disabled={!plan || !plan.items.length || Boolean(conflict)}
    >
      {error ? (
        <p className={um.errorText}>{error}</p>
      ) : !resolved ? (
        <p className={s.muted}>Loading the selected media…</p>
      ) : (
        <>
          <div className={um.fieldRow}>
            <label className={um.field}>
              <span className={um.label}>Start date ({tz})</span>
              <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Start date" />
            </label>
            <label className={um.field}>
              <span className={um.label}>Start time</span>
              <input type="time" step={60} className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={time} onChange={(e) => setTime(e.target.value)} aria-label="Start time" />
            </label>
          </div>
          {startMs === null && <span className={um.errorText}>Pick a valid date and start time.</span>}
          {plan && plan.items.length > 0 && (
            <p data-testid="bulk-summary">
              {plan.items.length} item{plan.items.length === 1 ? "" : "s"} back to back, {formatTime(startMs, tz)} → {formatTime(plan.end, tz)}
              {zonedParts(plan.end, tz).date !== date ? ` (${zonedParts(plan.end, tz).date})` : ""} · {formatDuration(total)}
            </p>
          )}
          {conflict && (
            <p className={um.errorText} data-testid="bulk-conflict">
              Overlaps “{conflict.title || "an existing item"}” at {formatTime(conflict.start, tz)}. Pick a later start time or move that item.
            </p>
          )}
          {plan && plan.placed.length > 0 && (
            <ol className={s.bulkList}>
              {plan.placed.map((x, i) => (
                <li key={`${x.video_id}-${i}`}>
                  <span className={s.muted}>{formatTime(x.start, tz)}</span>{" "}
                  {x.series_name ? `${x.series_name} · ` : ""}
                  {x.season_number != null && x.episode_number != null ? `S${x.season_number}E${x.episode_number} · ` : ""}
                  {x.name} <span className={s.muted}>({formatDuration(x.duration_sec)})</span>
                </li>
              ))}
            </ol>
          )}
          {skippedAll.length > 0 && (
            <p className={s.warnText} data-testid="bulk-skipped">
              Not added ({skippedAll.length}): {skippedAll.slice(0, 5).map((x) => `${x.name || x.series_name || `#${x.video_id || x.source_id}`} (${x.reason})`).join(", ")}
              {skippedAll.length > 5 ? "…" : ""}
            </p>
          )}
          {resolved.items.length === 0 && skippedAll.length === 0 && <p className={s.muted}>Nothing to add.</p>}
        </>
      )}
    </ActionDialog>
  );
}

const PlayoScheduler = () => {
  const { id } = useParams();
  const { can } = useRbac();
  const canView = can("livetv", "view");
  const canEdit = can("livetv", "edit");

  const [channel, setChannel] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState(null); // { item } | { defaults: { start } }
  const [dialog, setDialog] = useState(null); // 'fill' | 'copy' | 'repeat'
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [bulkPicks, setBulkPicks] = useState(null); // Media Library items picked for "Add from Media Library"
  const drag = useRef(null);
  const scrollRef = useRef(null);

  const tz = channel?.timezone || "UTC";
  const guideOnly = channel?.source_type === "external";

  useEffect(() => {
    if (!canView) return;
    getChannel(id)
      .then((ch) => {
        if (!ch) throw new Error("Channel not found");
        setChannel(ch);
        setAnchor(todayIn(ch.timezone || "UTC"));
      })
      .catch((e) => setLoadError(apiErrorMessage(e, "Channel not found")));
  }, [id, canView]);

  const days = useMemo(() => {
    if (!anchor) return [];
    if (view === "day") return [anchor];
    const first = weekStart(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(first, i));
  }, [anchor, view]);

  const reload = useCallback(async () => {
    if (!channel || !days.length) return;
    setLoading(true);
    try {
      const from = dayRangeUtc(days[0], tz).start;
      const to = dayRangeUtc(days[days.length - 1], tz).end;
      setItems(await loadRange(channel.id, from, to));
    } catch (e) {
      showToast(apiErrorMessage(e, "Could not load the schedule"), "error");
    } finally {
      setLoading(false);
    }
  }, [channel, days, tz]);

  useEffect(() => {
    reload();
  }, [reload]);

  // scroll to "now" (or 06:00) once per view
  useEffect(() => {
    if (!scrollRef.current || !anchor) return;
    const ppm = PX_PER_MIN[view];
    const nowP = zonedParts(Date.now(), tz);
    const minutes = days.includes(nowP.date) ? Math.max(0, nowP.minutes - 60) : 6 * 60;
    scrollRef.current.scrollTop = minutes * ppm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, anchor, channel]);

  if (!canView) {
    return (
      <div className={um.page}>
        <div className={um.emptyState}>
          <div className={um.emptyTitle}>No access</div>
          <div className={um.emptyMessage}>You don't have permission to view channels.</div>
        </div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className={um.page}>
        <Link to="/mainlayout/playo/channels" className={um.backLink}>
          ← Channels
        </Link>
        <div className={um.errorText}>{loadError}</div>
      </div>
    );
  }
  if (!channel || !anchor) return <div className={um.page}>Loading…</div>;

  const ppm = PX_PER_MIN[view];
  const step = view === "day" ? 1 : 7;
  const nowMs = Date.now();

  const openAddAt = (t) => setDrawer({ defaults: { start: snapStart(t, items) } });
  const nextFreeStart = () => {
    const { start, end } = dayRangeUtc(days[0], tz);
    const last = lastEndBefore(dayRangeUtc(days[days.length - 1], tz).end, items);
    const t = last && last >= start ? last : Math.max(start, Math.ceil(nowMs / (5 * MINUTE)) * 5 * MINUTE);
    return Math.min(t, end - MINUTE);
  };
  const addAfterLast = () => setDrawer({ defaults: { start: nextFreeStart() } });

  const columnClick = (dateStr) => (e) => {
    if (!canEdit || (e.target !== e.currentTarget && !e.target.dataset.gap)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    openAddAt(instantAtOffset(dateStr, tz, e.clientY - rect.top, ppm));
  };

  const onDrop = (dateStr) => async (e) => {
    e.preventDefault();
    const d = drag.current;
    drag.current = null;
    if (!d || !canEdit) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = instantAtOffset(dateStr, tz, e.clientY - rect.top - d.grabPx, ppm);
    const start = snapStart(raw, items, { ignoreId: d.item.id });
    if (start === new Date(d.item.start_time).getTime()) return;
    try {
      const { message } = await updateItem(channel.id, d.item.id, { start_time: toIso(start), duration_sec: d.item.duration_sec });
      showToast(message || `Moved to ${formatTime(start, tz)}`, /warning/i.test(message) ? "warning" : "success");
    } catch (err) {
      showToast(apiErrorMessage(err, "Could not move the item"), "error");
    }
    reload();
  };

  return (
    <div className={um.page}>
      <Link to="/mainlayout/playo/channels" className={um.backLink}>
        ← Channels
      </Link>
      <div className={um.header}>
        <div className={um.headerTitleWrap}>
          <h1 className={um.title}>
            {channel.name}
            <span className={`${s.pill} ${channel.status === "active" ? s.pill_ok : s.pill_muted}`}>{channel.status === "active" ? "Active" : "Draft"}</span>
          </h1>
          <div className={um.subtitle}>
            {guideOnly ? "External live stream · this is its TV guide. " : ""}Times shown in {tz}
          </div>
        </div>
        {canEdit && (
          <div className={um.headerActions}>
            {guideOnly ? (
              <button type="button" className={um.btnPrimary} onClick={addAfterLast}>
                Add guide entry
              </button>
            ) : (
              <>
                <button type="button" className={um.btnPrimary} onClick={() => setLibraryOpen(true)}>
                  Add from Media Library
                </button>
                <button type="button" className={um.btnSubtle} onClick={addAfterLast}>
                  Add item
                </button>
                <button type="button" className={um.btnSubtle} onClick={() => setDialog("fill")}>
                  Fill gaps with filler
                </button>
              </>
            )}
            <button type="button" className={um.btnSubtle} onClick={() => setDialog("copy")}>
              Copy day to…
            </button>
            <button type="button" className={um.btnSubtle} onClick={() => setDialog("repeat")}>
              Repeat…
            </button>
          </div>
        )}
      </div>

      <div className={s.layout}>
        <div className={s.main}>
          <div className={s.calToolbar}>
            <div className={um.tabs}>
              {["day", "week"].map((v) => (
                <button key={v} type="button" className={`${um.tab} ${view === v ? um.tabActive : ""}`} onClick={() => setView(v)}>
                  {v === "day" ? "Day" : "Week"}
                </button>
              ))}
            </div>
            <div className={s.navBtns}>
              <button type="button" className={um.btnGhost} onClick={() => setAnchor(addDays(anchor, -step))} aria-label="Previous">
                ‹
              </button>
              <button type="button" className={um.btnGhost} onClick={() => setAnchor(todayIn(tz))}>
                Today
              </button>
              <button type="button" className={um.btnGhost} onClick={() => setAnchor(addDays(anchor, step))} aria-label="Next">
                ›
              </button>
              <input type="date" className={`${um.input} ${s.dateTime}`} onClick={openPicker} value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} aria-label="Go to date" />
            </div>
            {loading && <span className={s.muted}>Loading…</span>}
          </div>

          <div className={s.calHead} style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
            <span />
            {days.map((d) => (
              <div key={d} className={`${s.dayHead} ${d === todayIn(tz) ? s.dayHeadToday : ""}`}>
                {weekdayLabel(d)}
              </div>
            ))}
          </div>
          <div className={s.calScroll} ref={scrollRef}>
            <div className={s.calGrid} style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
              <div className={s.hours} style={{ height: 24 * 60 * ppm }}>
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className={s.hourLabel} style={{ top: h * 60 * ppm }}>
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>
              {days.map((d) => {
                const range = dayRangeUtc(d, tz);
                const height = ((range.end - range.start) / MINUTE) * ppm;
                const gaps = findGaps(items, range.start, range.end);
                const nowInDay = nowMs >= range.start && nowMs < range.end;
                return (
                  <div
                    key={d}
                    className={s.dayCol}
                    style={{ height, backgroundSize: `100% ${60 * ppm}px` }}
                    onClick={columnClick(d)}
                    onDragOver={(e) => canEdit && e.preventDefault()}
                    onDrop={onDrop(d)}
                    data-testid={`day-${d}`}
                  >
                    {gaps.map((g) => {
                      const top = ((g.start - range.start) / MINUTE) * ppm;
                      const h = ((g.end - g.start) / MINUTE) * ppm;
                      return (
                        <div key={g.start} className={`${s.gap} ${guideOnly ? s.gapGuide : ""}`} style={{ top, height: h }} data-gap="1" data-testid="gap" title={`Gap ${formatTime(g.start, tz)}–${formatTime(g.end, tz)}: ${guideOnly ? "no guide entry" : "filler plays"}`}>
                          {h > 16 && (
                            <span data-gap="1">
                              {guideOnly ? "No guide entry" : `Gap · ${formatDuration((g.end - g.start) / 1000)} · filler`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {layoutDay(items, d, tz, ppm).map(({ item, top, height: h, clippedStart, clippedEnd }) => (
                      <button
                        type="button"
                        key={item.id}
                        className={`${s.item} ${item.type === "live" ? s.itemLive : ""} ${item.type === "filler" ? s.itemFiller : ""} ${item.type === "ad_break" ? s.itemAd : ""} ${item.channel_ready === false ? s.itemNotReady : ""}`}
                        style={{ top, height: h }}
                        draggable={canEdit}
                        onDragStart={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          drag.current = { item, grabPx: clippedStart ? 0 : e.clientY - r.top };
                          if (e.dataTransfer) e.dataTransfer.setData("text/plain", String(item.id));
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDrawer({ item });
                        }}
                        title={`${item.title || "Untitled"} · ${formatTime(item.start, tz)}–${formatTime(item.end, tz)}`}
                        data-testid={`item-${item.id}`}
                      >
                        <span className={s.itemTime}>
                          {clippedStart ? "…" : ""}
                          {formatTime(item.start, tz)}–{formatTime(item.end, tz)}
                          {clippedEnd ? "…" : ""}
                        </span>
                        <span className={s.itemTitle}>{item.title || (item.type === "ad_break" ? "Ad break" : "Untitled")}</span>
                        {item.channel_ready === false && h > 30 && <ReadyPill state={false} />}
                      </button>
                    ))}
                    {nowInDay && <div className={s.nowLine} style={{ top: ((nowMs - range.start) / MINUTE) * ppm }} />}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={s.legend}>
            {guideOnly ? (
              <span className={s.muted}>The live stream plays all the time; entries only label it in the TV guide.</span>
            ) : (
              <>
                <span className={s.legendGap} /> Gap (filler plays)
                <span className={`${s.legendItem} ${s.itemNotReady}`} /> Not channel-ready (filler plays until it is)
              </>
            )}
            {canEdit && <span className={s.muted}>Click an empty spot to add · drag an item to move it</span>}
          </div>
        </div>
        <aside className={s.side}>
          <PlayoPreview channel={channel} />
        </aside>
      </div>

      {drawer && (
        <div className={um.modalOverlay} onClick={() => setDrawer(null)}>
          <aside className={um.drawer} onClick={(e) => e.stopPropagation()}>
            <div className={um.drawerHeader}>
              <div className={um.drawerTitle}>{drawer.item ? drawer.item.title || "Edit item" : "Add to schedule"}</div>
            </div>
            <ItemForm
              channel={channel}
              item={drawer.item}
              defaults={drawer.defaults}
              items={items}
              canEdit={canEdit}
              onCancel={() => setDrawer(null)}
              onDone={() => {
                setDrawer(null);
                reload();
              }}
            />
          </aside>
        </div>
      )}
      {dialog === "fill" && <FillGapsDialog channel={channel} days={days} onCancel={() => setDialog(null)} onDone={() => { setDialog(null); reload(); }} />}
      {dialog === "copy" && <CopyDayDialog channel={channel} sourceDate={anchor} onCancel={() => setDialog(null)} onDone={() => { setDialog(null); reload(); }} />}
      {dialog === "repeat" && <RepeatDialog channel={channel} sourceDate={anchor} onCancel={() => setDialog(null)} onDone={() => { setDialog(null); reload(); }} />}
      <LibraryPicker
        open={libraryOpen}
        kind="content"
        onClose={() => setLibraryOpen(false)}
        onPickMany={(picked) => {
          if (picked.length) setBulkPicks(picked);
        }}
      />
      {bulkPicks && (
        <BulkAddDialog
          channel={channel}
          picks={bulkPicks}
          defaultStart={nextFreeStart()}
          onCancel={() => setBulkPicks(null)}
          onDone={(startMs) => {
            setBulkPicks(null);
            const day = zonedParts(startMs, tz).date;
            if (!days.includes(day)) setAnchor(day);
            else reload();
          }}
        />
      )}
    </div>
  );
};

export { ItemForm, FillGapsDialog, CopyDayDialog, RepeatDialog, BulkAddDialog };
export default PlayoScheduler;
